import { type SxProps, type Theme } from '@mui/material';
import { SkinStylesMap } from '../../hooks/useSkinStyles';
import { SKIN } from '../../types/enums';

export const ODDS_CHANGE_INDICATOR_DURATION = 2000;

export type OddsChangeDirection = 'up' | 'down';

const TRIANGLE_SIZE = 8;
const TRIANGLE_OFFSET = 6;
const ODDS_CHANGE_ANIMATION_DURATION = 0.4;
const BLINK_KEYFRAME_NAME = 'oddsChangeBlink';
const PREVIOUS_ODDS_KEYFRAME_NAME = 'previousOddsChangeValue';
const CURRENT_ODDS_KEYFRAME_NAME = 'currentOddsChangeValue';
const PREVIOUS_ODDS_EXIT_OFFSET_VAR = '--previous-odds-exit-offset';
const CURRENT_ODDS_ENTRY_OFFSET_VAR = '--current-odds-entry-offset';

const PREVIOUS_ODDS_EXIT_OFFSET: Record<OddsChangeDirection, string> = {
  up: '-100%',
  down: '100%',
};

const CURRENT_ODDS_ENTRY_OFFSET: Record<OddsChangeDirection, string> = {
  up: '100%',
  down: '-100%',
};

type TriangleBaseStyles = {
  width: number;
  height: number;
  position: 'absolute';
  borderStyle: 'solid';
  borderColor: 'transparent';
  borderRadius: number;
  animation: string;
};

type TriangleDirectionStyles = {
  top?: number;
  bottom?: number;
  right: number;
  borderWidth: string;
  borderRightColor?: string;
  borderBottomColor?: string;
};

export type OddsChangeIndicatorStyles = {
  triangle: SxProps<Theme>;
  odds: SxProps<Theme>;
};

const blinkKeyframes = {
  '16%': { opacity: 1 },
  '32%': { opacity: 0 },
  '48%': { opacity: 1 },
  '64%': { opacity: 0 },
  '80%': { opacity: 1 },
  '100%': { opacity: 0 },
};

const NEOBET_STYLES: OddsChangeIndicatorStyles = {
  triangle: {},
  odds: {
    display: 'inline-block',
    overflow: 'hidden',
    position: 'relative',
    whiteSpace: 'nowrap',
    fontWeight: { xs: 800, md: 800 },
  },
};

const NOOP_STYLES: OddsChangeIndicatorStyles = {
  triangle: { display: 'none' },
  odds: {},
};

const getTriangleBaseStyles = (isFlashing: boolean, blinkKeyframeName: string): TriangleBaseStyles => ({
  width: 0,
  height: 0,
  position: 'absolute',
  borderStyle: 'solid',
  borderColor: 'transparent',
  borderRadius: 0.5,
  animation: isFlashing ? `${blinkKeyframeName} ${ODDS_CHANGE_INDICATOR_DURATION / 1000}s ease-out` : 'none',
});

const getTriangleDirectionStyles = (direction: OddsChangeDirection): TriangleDirectionStyles => {
  const directionStyles: Record<OddsChangeDirection, TriangleDirectionStyles> = {
    up: {
      top: TRIANGLE_OFFSET,
      right: TRIANGLE_OFFSET,
      borderWidth: `0px ${TRIANGLE_SIZE}px ${TRIANGLE_SIZE}px 0px`,
      borderRightColor: 'success.400',
    },
    down: {
      bottom: TRIANGLE_OFFSET,
      right: TRIANGLE_OFFSET,
      borderWidth: `0px 0px ${TRIANGLE_SIZE}px ${TRIANGLE_SIZE}px`,
      borderBottomColor: 'error.300',
    },
  };

  return directionStyles[direction];
};

const previousOddsKeyframes = {
  '0%': {
    transform: 'translateY(0)',
  },
  '100%': {
    transform: `translateY(var(${PREVIOUS_ODDS_EXIT_OFFSET_VAR}))`,
  },
};

const currentOddsKeyframes = {
  '0%': {
    transform: `translateY(var(${CURRENT_ODDS_ENTRY_OFFSET_VAR}))`,
  },
  '100%': {
    transform: 'translateY(0)',
  },
};

export const getOddsChangeIndicatorSkinStyles = (
  isFlashing: boolean,
  direction: OddsChangeDirection,
  selected?: boolean
): SkinStylesMap<OddsChangeIndicatorStyles> => ({
  [SKIN.BET365]: () => NOOP_STYLES,
  [SKIN.GOLA]: () => NOOP_STYLES,
  [SKIN.NEOBET]: () => {
    return {
      triangle: {
        ...NEOBET_STYLES.triangle,
        ...getTriangleBaseStyles(isFlashing, BLINK_KEYFRAME_NAME),
        ...getTriangleDirectionStyles(direction),
        display: isFlashing ? 'block' : 'none',
        [`@keyframes ${BLINK_KEYFRAME_NAME}`]: blinkKeyframes,
      },
      odds: {
        ...NEOBET_STYLES.odds,
        [PREVIOUS_ODDS_EXIT_OFFSET_VAR]: PREVIOUS_ODDS_EXIT_OFFSET[direction],
        [CURRENT_ODDS_ENTRY_OFFSET_VAR]: CURRENT_ODDS_ENTRY_OFFSET[direction],
        WebkitTextFillColor: isFlashing ? 'transparent' : 'currentColor',
        ...(isFlashing &&
          selected && {
            WebkitTextStroke: '0px transparent !important',
            textShadow: 'none !important',
          }),
        '&::before, &::after': {
          color: 'inherit',
          display: isFlashing ? 'inline-block' : 'none',
          fontWeight: { xs: 800, md: 800 },
          inset: 0,
          lineHeight: 'inherit',
          position: 'absolute',
          textAlign: 'center',
          WebkitTextFillColor: 'currentColor',
          ...(isFlashing &&
            selected && {
              paintOrder: 'stroke fill',
              WebkitTextStroke: (theme: Theme) => `1.5px ${theme.palette.primary[700]}`,
              textShadow: (theme: Theme) => `0 1.5px 0 ${theme.palette.primary[700]}`,
            }),
        },
        '&::before': {
          animation: isFlashing
            ? `${PREVIOUS_ODDS_KEYFRAME_NAME} ${ODDS_CHANGE_ANIMATION_DURATION}s ease-out both`
            : 'none',
          content: 'attr(data-prev-odds)',
        },
        '&::after': {
          animation: isFlashing
            ? `${CURRENT_ODDS_KEYFRAME_NAME} ${ODDS_CHANGE_ANIMATION_DURATION}s ease-out both`
            : 'none',
          content: 'attr(data-current-odds)',
        },
        [`@keyframes ${PREVIOUS_ODDS_KEYFRAME_NAME}`]: previousOddsKeyframes,
        [`@keyframes ${CURRENT_ODDS_KEYFRAME_NAME}`]: currentOddsKeyframes,
      },
    };
  },
});
